import type { TiptapDoc } from '@odo/shared-ui/rich-editor/types';
import apiV2 from '../api/v2';
import { toApiError } from './apiErrors';

// Accounts > Orthodontist detail > Preferences & protocols - admin-editable
// mirror of the self-service /orthodontist/me/preferences and /me/protocols.

export interface OrthodontistPreference {
    key: string;
    title: string;
    description: string;
    enabled: boolean;
    enabledAt: string | null;
}

export async function fetchOrthodontistPreferences(
    orthodontistId: string
): Promise<OrthodontistPreference[]> {
    try {
        const response = await apiV2.get<{ success: boolean; data: OrthodontistPreference[] }>(
            `/orthodontist/preferences/${orthodontistId}`
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export async function updateOrthodontistPreference(
    orthodontistId: string,
    key: string,
    enabled: boolean
): Promise<OrthodontistPreference> {
    try {
        const response = await apiV2.put<{ success: boolean; data: OrthodontistPreference }>(
            `/orthodontist/preferences/${orthodontistId}/${encodeURIComponent(key)}`,
            { enabled }
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export interface OrthodontistProtocol {
    id: string;
    title: string;
    body: string;
    bodyDoc: TiptapDoc | null;
    createdAt: string;
    updatedAt: string;
}

export interface OrthodontistProtocolFields {
    title: string;
    bodyDoc: TiptapDoc;
}

export async function fetchOrthodontistProtocols(
    orthodontistId: string
): Promise<OrthodontistProtocol[]> {
    try {
        const response = await apiV2.get<{ success: boolean; data: OrthodontistProtocol[] }>(
            `/orthodontist/protocols/${orthodontistId}`
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export async function fetchProtocolsByPractice(
    practiceId: string
): Promise<OrthodontistProtocol[]> {
    try {
        const response = await apiV2.get<{ success: boolean; data: OrthodontistProtocol[] }>(
            `/orthodontist/protocols/by-practice/${practiceId}`
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export async function createOrthodontistProtocol(
    orthodontistId: string,
    fields: OrthodontistProtocolFields
): Promise<OrthodontistProtocol> {
    try {
        const response = await apiV2.post<{ success: boolean; data: OrthodontistProtocol }>(
            `/orthodontist/protocols/${orthodontistId}`,
            fields
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export async function updateOrthodontistProtocol(
    orthodontistId: string,
    id: string,
    fields: Partial<OrthodontistProtocolFields>
): Promise<OrthodontistProtocol> {
    try {
        const response = await apiV2.patch<{ success: boolean; data: OrthodontistProtocol }>(
            `/orthodontist/protocols/${orthodontistId}/${id}`,
            fields
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export async function deleteOrthodontistProtocol(
    orthodontistId: string,
    id: string
): Promise<void> {
    try {
        await apiV2.delete(`/orthodontist/protocols/${orthodontistId}/${id}`);
    } catch (err) {
        throw toApiError(err);
    }
}
